/**
 * 이상한 방 - 밀폐 실내 조명 리그
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2("시각적으로 찾을 수 없는 건 금지") ·
 * 4절(팔레트 설계 요건) · 7.2절(부하 특성) · `ASSETS.md` 9절(드로우콜이 병목)
 *
 * 공간은 **4벽 + 천장으로 완전히 밀폐된 20m × 20m급 실내**이고, 구획이 벽과 문으로 이어져 있다.
 * 야외가 아니므로 광원은 창과 실내 등이며, 이 파일이 그 둘을 전부 소유한다.
 * `scene.ts`는 렌더러 설정(톤매핑·그림자맵)만 책임지고 광원은 하나도 만들지 않는다.
 *
 * ## 이 파일의 제1 제약 - 조명은 예쁨이 아니라 판정 정합성 장치다
 *
 * 술래는 변화를 **눈으로** 찾는다. 조명이 만들 수 있는 최악의 버그는 "어둡다"가 아니라
 * **물건이 어둠에 묻혀 변화를 찾을 수 없게 되는 것**이며, 이는 원칙 2의 정면 위반이다.
 * 그래서 "그늘에도 최소 밝기가 남는가 / 그림자가 옅은가 / 톤매핑이 팔레트를 뭉개지 않는가"가
 * 취향보다 앞서고, 아래 상수는 전부 그 셋에서 역산됐다.
 *
 * ## 밝기 모델 - 상수를 감으로 고치지 말 것
 *
 * Three r155+의 물리 조명에서 램버트 확산면의 렌더 결과(선형광)는 다음 한 줄로 정해진다.
 *
 *   linear = (Σ irradiance) × albedo / π
 *
 *   - 방향광/반구광: irradiance = intensity × color × dotNL   (색은 채널별 선형값)
 *   - 반구광의 dotNL 자리: mix(groundColor, skyColor, 0.5 × n.y + 0.5)
 *   - 점광(칸델라):  irradiance = intensity × color / distance²   (decay = 2일 때)
 *
 * 그 뒤 `scene.ts`의 ACES 톤매핑이 걸린다. **Three의 ACES는 흔한 Narkowicz 근사가 아니라
 * 풀 ACES fit이며 입력에 1/0.6 밝기 프리스케일이 붙어 있다**(three #19621).
 * 즉 노출 1.0이 실제로는 ×1.667이다 - 이걸 모르고 계산하면 상수가 통째로 어긋난다.
 *
 * 아래 상수는 전부 이 파이프라인을 그대로 재현해 **역산·검증한 값**이며, 결과는 다음과 같다
 * (벽 = 팔레트 id8 살구크림, 256단계 화면 휘도 / "최소인접차" = 팔레트 인접 10색의 최소 화면 간격):
 *
 *   | 상황            | 벽  | 최소인접차 | id0(가장 어두운 색) |
 *   |-----------------|-----|-----------|--------------------|
 *   | 바닥 양지        | 205 |    14     |  18                |
 *   | 바닥 그림자 속    | 181 |    14     |  11                |
 *   | 측면 창 쪽       | 191 |    16     |  13                |
 *   | 측면 등짐        | 171 |    12     |   9                |
 *   | 밑면(책상 밑)    | 138 |     8     |   4                |
 *   | 창 없는 안쪽 구획 | 187 |    16     |  12                |
 *
 * 두 가지를 동시에 만족시킨 값이다.
 *  - **형태가 읽힌다**: 138~225로 87단계가 벌어져 면마다 밝기가 다르다.
 *  - **아무 데서도 색이 뭉개지지 않는다**: 팔레트 인접 색 간격이 최악(밑면)에도 8단계 남는다.
 *    평면 영역의 식별 한계가 2~3단계이므로 여유가 있다. 이게 "색 변경 동사가 성립한다"의 수치적 정의다.
 *
 * 위 표는 exposure 1.0 기준이다. 가장 밝은 조합(창광 + 천장등 바로 아래)에서도 id8·id9는
 * 8단계 남고 255에 붙지 않는다(최대 239) - 여유가 크지 않다.
 * **광량을 올릴 거면 `scene.ts`의 노출을 같이 내려야 한다.**
 *
 * ## 채움광이 둘인 이유 - 반구광만으로는 수직면을 구별할 수 없다
 *
 * 반구광의 방향 의존성은 y축뿐이라 동쪽을 보는 벽과 서쪽을 보는 벽에 **똑같은 양**을 준다.
 * 그래서 반구광 세기만 올려 어두운 곳을 살리면 수직면이 전부 같은 밝기가 되어 방이 납작해진다
 * (실측: 반구광만으로 채우면 양지↔밑면 차가 29단계까지 좁아졌다).
 * 그늘진 면은 **수평 방향의 약한 보조광(fill)**이 맡고, 반구광은 바닥·천장 반사광 흉내만 낸다.
 *
 * ## 그림자 예산 - 캐스터는 딱 하나다
 *
 * 그림자 캐스팅 광원 하나당 씬을 한 번 더 그리고, 점광 그림자는 큐브맵이라 **여섯 번**이다.
 * 오브젝트 300~500개 환경에서 방마다 천장등이 그림자를 지면 프레임이 무너진다.
 * 그래서 그림자를 지는 광원은 **창광(방향광) 하나뿐**이고 나머지는 밝기만 기여한다.
 *
 * ## 셸 메시에 대한 요구 (roomShell 소유자에게)
 *
 * **벽·천장은 `castShadow = false`여야 한다.** 창광은 "이미 실내에 들어온 빛"을 대신하는
 * 방향광이라, 천장이 그림자를 지면 실내 전체가 그림자에 들어가 창광이 통째로 사라진다.
 * 셸은 `receiveShadow = true`만 켠다. 편의를 위해 `disableShadowCasting()`을 공개해 둔다.
 */

import {
  DirectionalLight,
  Group,
  HemisphereLight,
  PointLight,
  Vector3,
  type Object3D,
  type Scene,
} from 'three'

import {
  openingRect,
  openingSide,
  requireRoom,
  type CellRect,
  type FloorPlan,
  type Room,
  type WindowOpening,
} from '../core/floorPlan'
import { cellToWorld } from '../core/grid'
import { CELL_SIZE_METERS, type Cell3, type RoomBounds } from '../core/types'
import { BOOTSTRAP_LIGHT_NAME } from './scene'

/* --- 앰비언트(반구광) ------------------------------------------------------------ */

/** 천장 반사광. 따뜻한 크림색 - 실내 벽·천장이 온색 파스텔이라는 전제에서 온 값이다. */
const HEMISPHERE_SKY_COLOR = 0xfff2e4

/**
 * 바닥 반사광. 나무·러그에서 튀어 오르는 색이라 베이지다.
 * **이 값이 물건 밑면의 최소 밝기를 결정한다.** 어둡게 잡으면 책상 밑 소품이 검게 죽어
 * "가려진 위치는 허용하되 찾을 수는 있어야 한다"(3.1절)가 깨진다.
 * 밑면에서도 팔레트 인접 색이 8단계 벌어지는 하한선이 이 값이다.
 */
const HEMISPHERE_GROUND_COLOR = 0xdccebe

/**
 * 반구광 세기. 이 값만으로 어두운 곳을 살리려 들면 방이 납작해진다 -
 * 이유는 파일 상단 "채움광이 둘인 이유" 참고. 여기서 멈추고 나머지는 보조광이 맡는다.
 */
const HEMISPHERE_INTENSITY = 1.6

/* --- 창광(주 광원, 유일한 그림자 캐스터) ----------------------------------------- */

/** 창밖 하늘색. 실내등(온색)과 대비되도록 **살짝** 차갑게. 과하면 실내가 시퍼레진다. */
const KEY_LIGHT_COLOR = 0xdce9ff

/** 창광 세기. 이 값이 양지(205)와 그림자 속(181)의 차를 만든다 - 상단 표 참고. */
const KEY_LIGHT_INTENSITY = 1.95

/**
 * 창광이 아래로 기우는 정도(수평 성분 1 기준).
 * 1.15면 대략 고도 49°다. 더 눕히면 20m를 가로지르는 긴 그림자가 생겨 판독을 방해하고,
 * 더 세우면 수직면(벽·가구 앞면)이 어두워져 벽 소품이 묻힌다.
 */
const KEY_LIGHT_PITCH = 1.15

/** 창이 하나도 없는 평면도에서 쓰는 기본 창광 방향(빛이 진행하는 방향). */
const KEY_LIGHT_FALLBACK_DIRECTION: Readonly<{ x: number; y: number; z: number }> = {
  x: 0.45,
  y: -KEY_LIGHT_PITCH,
  z: 0.3,
}

/** 광원을 방 바깥으로 밀어내는 거리 = 바운딩 반지름 × 이 값. 방향광이라 그림자 카메라 위치용이다. */
const KEY_LIGHT_DISTANCE_FACTOR = 1.6

/**
 * 그림자 맵 한 변. 20m 공간을 2048로 덮으면 텍셀 ≈ 1.4cm로 셀(25cm)의 1/18이라 충분하다.
 * 4096으로 올리면 깊이 버퍼만 64MB라 모바일이 먼저 죽는다(7.0절: 모바일 브라우저 포함).
 */
const SHADOW_MAP_SIZE = 2048

/** 그림자 아크네 제거용 상수 바이어스. 텍셀이 1.4cm이므로 이보다 크면 피터패닝이 시작된다. */
const SHADOW_BIAS = -0.0004

/**
 * 노멀 방향 오프셋(월드 미터).
 * 접시(두께 0.06m)·러그 같은 얇은 소품이 자기 그림자에 먹히지 않을 최소값이며,
 * 0.05까지 올리면 그 얇은 소품들의 그림자가 통째로 떠 버린다.
 */
const SHADOW_NORMAL_BIAS = 0.02

/**
 * 그림자 농도(0~1). **이 게임에서 짙은 그림자는 버그다.**
 * 0.85면 그림자 안에도 창광의 15%가 남아 양지 205 : 그늘 181로 24단계 차가 된다 -
 * 접지감은 나오되 물건이 어둠에 묻히지 않는 지점이며, 덤으로 그림자맵 경계의
 * 계단 아티팩트까지 같이 흐려진다.
 */
const SHADOW_INTENSITY = 0.85

/**
 * 그림자 카메라 깊이 여유(m). 스피어 밖으로 살짝 튀어나온 지오메트리(벽 두께 등)를 위한 값이다.
 * 근·원평면 자체는 "광원에서 바운딩 스피어의 앞면/뒷면까지"로 정확히 계산한다 -
 * 배수로 어림잡으면 원평면이 스피어 뒷면에 못 미쳐 **방 안쪽 절반의 그림자가 통째로 사라지고**,
 * 화면에서는 "저쪽 방만 그림자가 없네"로 보여 조명 문제로 보이지도 않는다.
 */
const SHADOW_DEPTH_MARGIN_METERS = 1

/* --- 보조광(수평 채움, 그림자 없음) ---------------------------------------------- */

/**
 * 보조광 색. 창광이 차가우므로 반대쪽은 따뜻하게 둔다.
 * 이 온·냉 대비가 "면마다 밝기만 다른" 상태와 "면마다 빛의 성질이 다른" 상태를 가른다 -
 * 후자가 로우폴리에서 형태를 읽게 만드는 실제 장치다.
 */
const FILL_LIGHT_COLOR = 0xffe8d5

/**
 * 보조광 세기. 창광의 약 1/3이며, 창을 등진 면(171)이 창 쪽 면(191)보다
 * 확실히 어둡되 색이 뭉개지지는 않는 값이다. 더 올리면 창의 방향성이 사라진다.
 */
const FILL_LIGHT_INTENSITY = 0.7

/**
 * 보조광이 아래로 기우는 정도. 창광(1.15)보다 훨씬 눕혀 **수직면을 노린다** -
 * 세우면 반구광과 하는 일이 같아져 보조광을 둔 의미가 없어진다.
 */
const FILL_LIGHT_PITCH = 0.35

/* --- 천장등(구획별 실내 조명) ----------------------------------------------------- */

/**
 * 천장등 색. **2700K의 색도(≈ #FFA957)를 그대로 쓰지 않는다.**
 * 실제 색온도를 곧이곧대로 넣으면 화면이 나트륨등처럼 주황으로 덮여 팔레트의 색상 구별이 죽는다
 * (4절: 색상뿐 아니라 명도까지 벌려 둔 사다리가 무의미해진다).
 * 카메라가 부분적으로 화이트밸런스를 잡은 상태를 흉내 내 채도를 절반쯤 눌렀다 -
 * "따뜻해 보이되 색을 먹지 않는" 지점이다.
 */
const CEILING_LIGHT_COLOR = 0xffd9b3

/**
 * 천장등 세기(칸델라). decay 2에서 2.4m 아래 바닥이 187, 등 바로 1m 아래가 219가 된다.
 * 창가 구획 바닥(205)보다 안쪽 구획(187)이 어둡게 남는 것이 **구획별 밝기 차의 실체**이며,
 * 밝기 차가 작아 보여도 색(온색 vs 냉색)이 같이 갈리므로 화면에서는 분명히 다른 방으로 읽힌다.
 */
const CEILING_LIGHT_INTENSITY = 3

/**
 * 점광의 물리 감쇠(역제곱). 천장등·창가 산란광이 함께 쓴다.
 * 낮추면 감쇠가 완만해져 **벽 너머 누출이 급증한다** - 아래 사거리 설명 참고.
 */
const POINT_LIGHT_DECAY = 2

/**
 * 사거리 = 구획 바운딩 반지름 × 이 값.
 *
 * 성능 상수가 아니라 **벽 누출 제한 장치**다. 천장등은 그림자를 지지 않으므로 벽을 그대로
 * 통과해 옆 구획을 비춘다. 옆방이 환하게 새면
 * 구획을 나눈 의미가 사라진다. 1.15가 "자기 구획 구석은 닿고 옆 구획 깊은 곳은 못 미치는" 지점이다.
 */
const CEILING_LIGHT_RANGE_FACTOR = 1.15

/** 천장면에서 등이 내려온 거리(m). 천장 평면에 딱 붙이면 천장 자체가 새까맣게 남는다. */
const CEILING_LIGHT_DROP_METERS = 0.25

/** 등 하나가 담당하는 목표 간격(m). 이보다 넓은 구획은 등을 나눠 단다. */
const TARGET_LAMP_SPACING_METERS = 4

/** 한 축당 최대 등 개수. 넓은 홀에서 등이 무한정 늘어나 광원 예산을 독식하는 것을 막는다. */
const MAX_LAMPS_PER_AXIS = 2

/**
 * 점광(천장등 + 창가 산란광) 총 예산.
 *
 * Three의 포워드 렌더링은 **픽셀마다 광원 배열을 전부 순회한다** - 점광 개수가 곧 픽셀 셰이더
 * 비용이며, 화면에 보이지 않는 옆방 등도 똑같이 비용을 낸다. 12가 20m 실내에서 프레임을
 * 지키는 선이고, 성능이 모자라면 **가장 먼저 내려야 할 숫자가 이것**이다.
 * 방향광 2개와 반구광 1개는 거리 계산이 없어 훨씬 싸므로 예산 밖이다.
 */
const MAX_POINT_LIGHTS = 12

/** 창가 산란광 총 개수 상한. 나머지 예산이 천장등 몫이 된다. */
const MAX_WINDOW_LIGHTS = 3

/**
 * 천장등 예산. **단, 구획당 첫 등은 이 예산보다 우선한다**(아래 `selectLamps`).
 * 등이 하나도 없는 구획은 분위기가 아니라 판정 문제이기 때문이다 - 그 방의 변화를 찾을 수 없다.
 */
const CEILING_LIGHT_BUDGET = MAX_POINT_LIGHTS - MAX_WINDOW_LIGHTS

/* --- 창가 산란광 ----------------------------------------------------------------- */

/** 창으로 쏟아지는 하늘 산란광. 창광(방향광)보다 조금 더 차갑다. */
const WINDOW_LIGHT_COLOR = 0xd6e6ff

/** 창가 산란광 세기(칸델라). 창면 0.8m 앞이 225로 떠 "창가만 환한" 인상을 만든다. */
const WINDOW_LIGHT_INTENSITY = 2

/** 창가 산란광 사거리(m). 창 주변만 밝히는 국소 광원이라 짧다. */
const WINDOW_LIGHT_RANGE_METERS = 4.5

/** 창면에서 실내 쪽으로 광원을 들여놓는 거리(m). 벽면에 박히면 벽만 태운다. */
const WINDOW_LIGHT_INSET_METERS = 0.4

/* --- 공통 --------------------------------------------------------------------- */

const HALF = 0.5

/** 리그 그룹 이름. 디버그 UI가 씬 트리에서 조명 전체를 한 번에 집어낼 수 있게 공개한다. */
export const LIGHTING_RIG_GROUP_NAME = 'lighting-rig'

/* --- 순수 계산 ---------------------------------------------------------------- */

/** 미터 단위 축 정렬 박스. 구획·창의 월드 위치 계산의 공용 중간 표현이다. */
export type WorldBox = Readonly<{ min: Vector3; max: Vector3; center: Vector3; size: Vector3 }>

/**
 * 셀 영역(최소 코너 + 크기) → 월드 AABB. 축척 곱셈을 손으로 하지 않고 `cellToWorld`를
 * 거친다(프로젝트 좌표 계약). 그건 셀 **중심**을 주므로 최소 코너를 얻으려면 반 칸을 뺀다.
 */
export function cellBoxToWorld(origin: Cell3, size: Cell3): WorldBox {
  const originCenter = cellToWorld(origin)
  const halfCell = CELL_SIZE_METERS * HALF

  const min = new Vector3(
    originCenter.x - halfCell,
    originCenter.y - halfCell,
    originCenter.z - halfCell,
  )
  const extent = new Vector3(
    size.x * CELL_SIZE_METERS,
    size.y * CELL_SIZE_METERS,
    size.z * CELL_SIZE_METERS,
  )

  return {
    min,
    max: min.clone().add(extent),
    center: min.clone().addScaledVector(extent, HALF),
    size: extent,
  }
}

/**
 * 평면도의 2D 사각형 + 높이 구간 → 월드 AABB.
 *
 * 평면도는 2D(x·z)이고 높이는 `bounds.sizeY` 하나로 균일하다(`floorPlan.ts` 헤더).
 * 조명은 3D 위치가 필요하므로 여기서 y 구간을 붙인다.
 */
export function rectToWorldBox(rect: CellRect, originY: number, sizeY: number): WorldBox {
  return cellBoxToWorld(
    { x: rect.x, y: originY, z: rect.z },
    { x: rect.sizeX, y: sizeY, z: rect.sizeZ },
  )
}

/**
 * 구획 하나의 천장등 위치를 계산한다(월드 미터).
 *
 * 넓은 구획은 등 하나로 못 덮는다 - 역제곱 감쇠라 중앙만 타고 구석은 죽는다.
 * 그래서 `TARGET_LAMP_SPACING_METERS` 간격의 격자로 나누고, 각 칸의 **중심**에 하나씩 단다.
 * 반환 순서는 항상 x → z 안정 순서라 예산 절단(아래 `selectLamps`)이 결정적이다.
 *
 * `room.rect`는 벽을 제외한 **내부** 영역이므로(`floorPlan.ts` 규약) 등이 벽 속에 박히지 않는다.
 */
export function ceilingLampPositions(room: Room, bounds: RoomBounds): readonly Vector3[] {
  const box = rectToWorldBox(room.rect, 0, bounds.sizeY)
  const countX = lampCountForSpan(box.size.x)
  const countZ = lampCountForSpan(box.size.z)
  const lampY = box.max.y - CEILING_LIGHT_DROP_METERS

  const positions: Vector3[] = []
  for (let indexX = 0; indexX < countX; indexX += 1) {
    for (let indexZ = 0; indexZ < countZ; indexZ += 1) {
      positions.push(
        new Vector3(
          box.min.x + (box.size.x * (indexX + HALF)) / countX,
          lampY,
          box.min.z + (box.size.z * (indexZ + HALF)) / countZ,
        ),
      )
    }
  }

  return positions
}

/** 평면도 전체를 감싸는 월드 AABB. 창광·보조광의 배치 거리와 그림자 카메라의 공통 척도다. */
export function planWorldBox(bounds: RoomBounds): WorldBox {
  return cellBoxToWorld(
    { x: 0, y: 0, z: 0 },
    { x: bounds.sizeX, y: bounds.sizeY, z: bounds.sizeZ },
  )
}

/** 한 축의 길이를 목표 간격으로 나눈 등 개수. 최소 1개, 상한은 광원 예산이 정한다. */
function lampCountForSpan(spanMeters: number): number {
  const raw = Math.round(spanMeters / TARGET_LAMP_SPACING_METERS)
  return Math.min(Math.max(raw, 1), MAX_LAMPS_PER_AXIS)
}

/** 예산 절단용 후보. `rank`가 작을수록 먼저 살아남는다. */
type LampCandidate = Readonly<{ position: Vector3; rangeMeters: number; rank: number }>

/**
 * 천장등 후보를 예산 안으로 줄인다.
 *
 * **구획당 첫 등은 예산을 무시하고 전부 남긴다.** 예산으로 잘리는 것은 둘째 등부터다.
 * 단순히 상한으로 자르면 구획 수가 예산을 넘는 순간 어떤 방이 통째로 캄캄해지고
 * (기본 평면도가 구획 9개 / 예산 9라 실제로 아슬아슬하다), 그건 분위기가 아니라
 * **그 방의 변화를 찾을 수 없게 되는 문제**다(원칙 2). 광원 하나 더 그리는 쪽이 싸다.
 * 남은 예산으로 채우는 둘째 등부터는 넓은 구획이 우선이다 - 넓을수록 등 하나로는 구석이 죽는다.
 */
function selectLamps(plan: FloorPlan): readonly LampCandidate[] {
  const byArea = [...plan.rooms].sort(
    (a, b) => b.rect.sizeX * b.rect.sizeZ - a.rect.sizeX * a.rect.sizeZ,
  )

  const primary: LampCandidate[] = []
  const extra: LampCandidate[] = []

  byArea.forEach((room, roomIndex) => {
    const box = rectToWorldBox(room.rect, 0, plan.bounds.sizeY)
    const rangeMeters = Math.hypot(box.size.x, box.size.z) * HALF * CEILING_LIGHT_RANGE_FACTOR

    for (const [lampIndex, position] of ceilingLampPositions(room, plan.bounds).entries()) {
      // 순번(lampIndex)이 1차 키, 구획 크기 순위(roomIndex)가 2차 키.
      const candidate = { position, rangeMeters, rank: lampIndex * plan.rooms.length + roomIndex }
      if (lampIndex === 0) primary.push(candidate)
      else extra.push(candidate)
    }
  })

  const extraBudget = Math.max(0, CEILING_LIGHT_BUDGET - primary.length)
  return [...primary, ...extra.sort((a, b) => a.rank - b.rank).slice(0, extraBudget)]
}

/**
 * 창이 실내를 향하는 수평 방향(= 빛이 진행하는 방향).
 *
 * `openingSide`가 창이 그 구획의 어느 벽에 뚫렸는지를 알려 준다.
 * 'north'는 구획의 +z 벽이므로 빛은 -z로 들어온다 - 나머지도 같은 논리다.
 * 벽에 붙지 않은 창(side가 null)은 평면도 오류이므로 조용히 넘기지 않는다.
 */
export function windowInwardDirection(plan: FloorPlan, window: WindowOpening): Vector3 {
  const room = requireRoom(plan, window.roomId)
  const side = openingSide(room, window.axis, window.cell)

  switch (side) {
    case 'north':
      return new Vector3(0, 0, -1)
    case 'south':
      return new Vector3(0, 0, 1)
    case 'east':
      return new Vector3(-1, 0, 0)
    case 'west':
      return new Vector3(1, 0, 0)
    default:
      throw new RangeError(
        `창 '${window.id}'가 구획 '${window.roomId}'의 어느 벽에도 붙어 있지 않다. ` +
          `조명은 창이 실내를 향하는 방향을 이 관계에서만 알 수 있다.`,
      )
  }
}

/**
 * 창들에서 창광의 진행 방향을 역산한다.
 *
 * 창들의 실내 방향을 평균 낸 뒤 아래로 기울인다. 창이 마주 보게 뚫려 평균이 상쇄되면
 * 빛이 정수리에서만 떨어져 벽에 붙은 소품이 전부 어두워지므로, 그때는 기본 방향으로 물러난다.
 *
 * 이건 예외 처리가 아니라 **흔한 경우**다 - 기본 평면도는 네 벽에 창 12개가 대칭으로 뚫려 있어
 * 합이 정확히 0이고 실제로 이 폴백을 탄다. 사방이 창이면 어느 방향이든 근거가 같으므로
 * 결정적인 고정값을 쓰는 편이 낫다.
 */
export function keyLightDirection(plan: FloorPlan): Vector3 {
  const sum = new Vector3()
  for (const window of plan.windows) sum.add(windowInwardDirection(plan, window))

  if (sum.lengthSq() < Number.EPSILON) {
    return new Vector3(
      KEY_LIGHT_FALLBACK_DIRECTION.x,
      KEY_LIGHT_FALLBACK_DIRECTION.y,
      KEY_LIGHT_FALLBACK_DIRECTION.z,
    ).normalize()
  }

  // 수평 방향만 창에서 오고, 아래로 기우는 정도는 상수가 정한다(그림자 길이 제어).
  return sum.normalize().setY(-KEY_LIGHT_PITCH).normalize()
}

/* --- 광원 조립 ---------------------------------------------------------------- */

/** 반구광. 유일한 채움광이며 그림자 속 최소 밝기를 혼자 책임진다. */
function createAmbientLight(): HemisphereLight {
  const light = new HemisphereLight(
    HEMISPHERE_SKY_COLOR,
    HEMISPHERE_GROUND_COLOR,
    HEMISPHERE_INTENSITY,
  )
  light.name = 'light-ambient-hemisphere'
  return light
}

/**
 * 창광(방향광) + 그림자 카메라.
 *
 * 그림자 카메라는 평면도 전체의 바운딩 스피어를 덮는 정사영이다.
 * 구획별로 잘라 맞추면 텍셀 밀도는 올라가지만 카메라를 매 프레임 갱신해야 하고,
 * 20m/2048 = 1.4cm면 셀(25cm)의 1/18이라 이미 충분하다 - 복잡도를 살 이유가 없다.
 */
function createKeyLight(planBox: WorldBox, direction: Vector3): DirectionalLight {
  const radius = planBox.size.length() * HALF
  // 배치 거리와 그림자 깊이 범위가 같은 값에서 나와야 한다. 둘이 갈리면 원평면이 어긋난다.
  const distance = radius * KEY_LIGHT_DISTANCE_FACTOR

  const light = new DirectionalLight(KEY_LIGHT_COLOR, KEY_LIGHT_INTENSITY)
  light.name = 'light-key-window'
  light.position.copy(planBox.center).addScaledVector(direction, -distance)
  light.target.position.copy(planBox.center)
  light.target.name = 'light-key-window-target'

  light.castShadow = true
  light.shadow.mapSize.setScalar(SHADOW_MAP_SIZE)
  light.shadow.bias = SHADOW_BIAS
  light.shadow.normalBias = SHADOW_NORMAL_BIAS
  light.shadow.intensity = SHADOW_INTENSITY

  // 정사영이라 좌우상하는 바운딩 스피어 반지름이면 광원이 어느 방향을 보든 평면도 전체를 덮는다.
  // 깊이는 광원 거리 ± 반지름으로 잡는다 - 배수 어림값은 반드시 어느 한쪽을 잘라 먹는다.
  const shadowCamera = light.shadow.camera
  shadowCamera.left = -radius
  shadowCamera.right = radius
  shadowCamera.top = radius
  shadowCamera.bottom = -radius
  shadowCamera.near = Math.max(HALF, distance - radius - SHADOW_DEPTH_MARGIN_METERS)
  shadowCamera.far = distance + radius + SHADOW_DEPTH_MARGIN_METERS
  shadowCamera.updateProjectionMatrix()

  return light
}

/**
 * 보조광(수평 채움). 창광의 **수평 반대편**에서 얕게 들어온다.
 *
 * 하는 일은 하나다 - 창을 등진 수직면이 검게 죽는 것을 막는 것.
 * 반구광은 y축으로만 방향성이 있어 이 일을 대신할 수 없다(파일 상단 "채움광이 둘인 이유").
 * 그림자는 지지 않으므로 예산에도 걸리지 않는다.
 */
function createFillLight(planBox: WorldBox, keyDirection: Vector3): DirectionalLight {
  const direction = new Vector3(-keyDirection.x, 0, -keyDirection.z)

  // 창광이 정수리에서 수직으로 떨어지는 경우(수평 성분 0) 반대 방향이 정의되지 않는다.
  // 그때는 아무 수평 방향이나 잡으면 되며, +x를 쓴다 - 어느 쪽이든 대칭이라 결과가 같다.
  if (direction.lengthSq() < Number.EPSILON) direction.set(1, 0, 0)

  direction.normalize().setY(-FILL_LIGHT_PITCH).normalize()

  const radius = planBox.size.length() * HALF
  const light = new DirectionalLight(FILL_LIGHT_COLOR, FILL_LIGHT_INTENSITY)
  light.name = 'light-fill'
  light.position.copy(planBox.center).addScaledVector(direction, -radius * KEY_LIGHT_DISTANCE_FACTOR)
  light.target.position.copy(planBox.center)
  light.target.name = 'light-fill-target'
  light.castShadow = false

  return light
}

/** 천장등 하나. 그림자는 지지 않는다(파일 상단 "그림자 예산" 참고). */
function createCeilingLight(candidate: LampCandidate, index: number): PointLight {
  const light = new PointLight(
    CEILING_LIGHT_COLOR,
    CEILING_LIGHT_INTENSITY,
    candidate.rangeMeters,
    POINT_LIGHT_DECAY,
  )
  light.name = `light-ceiling-${index}`
  light.position.copy(candidate.position)
  light.castShadow = false
  return light
}

/** 창가 산란광 하나. 창면에서 실내 쪽으로 조금 들여 놓는다. */
function createWindowLight(plan: FloorPlan, window: WindowOpening): PointLight {
  // 유리면의 중심 = 개구부 사각형(가로) × [sillCell, sillCell + heightCells)(세로).
  const box = rectToWorldBox(
    openingRect(window.axis, window.cell, window.width),
    window.sillCell,
    window.heightCells,
  )

  const light = new PointLight(
    WINDOW_LIGHT_COLOR,
    WINDOW_LIGHT_INTENSITY,
    WINDOW_LIGHT_RANGE_METERS,
    POINT_LIGHT_DECAY,
  )
  light.name = `light-window-${window.id}`
  light.position
    .copy(box.center)
    .addScaledVector(windowInwardDirection(plan, window), WINDOW_LIGHT_INSET_METERS)
  light.castShadow = false
  return light
}

/* --- 공개 API ------------------------------------------------------------------ */

/**
 * 조명 리그. 컨테이너는 `Readonly`지만 Three 객체 내부의 제자리 변경은 의도된 사용법이다
 * (렌더 레이어의 불변성 예외 - `scene.ts` 헤더 참고).
 */
export type LightingRig = Readonly<{
  /** 모든 광원을 담은 그룹. 씬에서 조명만 통째로 떼어내려면 이것만 다루면 된다. */
  group: Group
  /** 유일한 그림자 캐스터. 노출·시간대 연출을 붙일 때의 손잡이다. */
  keyLight: DirectionalLight
  /** 창을 등진 면을 살리는 수평 보조광. 그림자를 지지 않는다. */
  fillLight: DirectionalLight
  ambient: HemisphereLight
  ceilingLights: readonly PointLight[]
  windowLights: readonly PointLight[]
  dispose: () => void
}>

/**
 * 오브젝트와 그 하위 전체의 그림자 캐스팅을 끈다.
 *
 * 벽·천장 셸이 그림자를 지면 창광이 실내에 도달하지 못해 리그 전체가 무의미해진다
 * (파일 상단 "셸 메시에 대한 요구"). 셸 소유자가 한 줄로 계약을 지킬 수 있게 공개한다.
 */
export function disableShadowCasting(root: Object3D): void {
  root.traverse((node) => {
    node.castShadow = false
  })
}

/**
 * 평면도를 받아 밀폐 실내용 조명 리그를 씬에 세운다.
 *
 * 구성: 반구광 1 + 창광(그림자 캐스터) 1 + 보조광 1 + 천장등(구획당 최소 1) + 창가 산란광 최대 3.
 * 점광 개수가 곧 픽셀 셰이더 비용이므로 상한은 취향이 아니라 예산이다(`MAX_POINT_LIGHTS`).
 */
export function createLightingRig(scene: Scene, plan: FloorPlan): LightingRig {
  assertLightablePlan(plan)
  removeBootstrapLights(scene)

  const group = new Group()
  group.name = LIGHTING_RIG_GROUP_NAME

  const planBox = planWorldBox(plan.bounds)
  const keyDirection = keyLightDirection(plan)

  const ambient = createAmbientLight()
  const keyLight = createKeyLight(planBox, keyDirection)
  const fillLight = createFillLight(planBox, keyDirection)
  // 방향광의 방향은 position − target으로 정해진다. target을 씬 그래프에 넣지 않으면
  // 월드 행렬이 갱신되지 않아 조용히 원점을 계속 바라본다.
  group.add(ambient, keyLight, keyLight.target, fillLight, fillLight.target)

  const ceilingLights = selectLamps(plan).map(createCeilingLight)
  // 창가 산란광은 개수 상한만 걸고 평면도 순서를 그대로 따른다.
  // 창은 천장등과 달리 "어느 구획도 빠뜨리면 안 된다"는 제약이 없어 순위 계산이 필요 없다.
  const windowLights = plan.windows
    .slice(0, MAX_WINDOW_LIGHTS)
    .map((window) => createWindowLight(plan, window))

  group.add(...ceilingLights, ...windowLights)
  scene.add(group)

  const dispose = (): void => {
    group.removeFromParent()
    // 그림자맵은 광원의 dispose()에 포함되지 않는다. 빠뜨리면 리그를 다시 세울 때마다
    // 2048² 깊이 텍스처가 VRAM에 쌓인다(HMR이 이 경로를 자주 탄다).
    keyLight.shadow.dispose()
    for (const light of [ambient, keyLight, fillLight, ...ceilingLights, ...windowLights]) {
      light.dispose()
    }
  }

  return {
    group,
    keyLight,
    fillLight,
    ambient,
    ceilingLights,
    windowLights,
    dispose,
  }
}

/**
 * **조명이 아무것도 못 하게 되는 입력**만 막는다. 평면도 전반의 정합성은
 * `floorPlan.ts`의 `assertValidFloorPlan`이 담당하며 여기서 중복하지 않는다 -
 * 검증이 두 벌이면 규칙이 갈라지고, 그때부터 어느 쪽이 진실인지 알 수 없게 된다.
 * 구획이 0개면 천장등이 안 생겨 "왜 안쪽이 캄캄하지"로만 보이는데 원인은 평면도다.
 */
function assertLightablePlan(plan: FloorPlan): void {
  if (plan.rooms.length === 0) {
    throw new RangeError(`평면도 '${plan.id}'에 구획이 하나도 없다. 천장등을 배치할 곳이 없다.`)
  }

  if (plan.bounds.sizeY > 0) return

  throw new RangeError(
    `평면도 '${plan.id}'의 천장 높이(bounds.sizeY)가 ${plan.bounds.sizeY}다. ` +
      `천장등을 매달 높이가 없다.`,
  )
}

/**
 * `scene.ts`가 검은 화면 방지용으로 붙여 둔 임시 광원을 걷어낸다.
 *
 * 리그가 붙기 전까지 씬이 완전히 캄캄하면 조립 중 어느 단계가 깨졌는지 분간이 안 된다.
 * 그래서 부트스트랩 광원이 존재하고, **정식 리그가 서는 순간 반드시 사라져야 한다** -
 * 남아 있으면 위 밝기 모델의 전제(반구광 하나)가 깨져 그림자가 통째로 옅어진다.
 */
function removeBootstrapLights(scene: Scene): void {
  const bootstrapLights = scene.children.filter((child) => child.name === BOOTSTRAP_LIGHT_NAME)

  for (const light of bootstrapLights) {
    light.removeFromParent()
    if ('dispose' in light && typeof light.dispose === 'function') light.dispose()
  }
}
